import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function WhyEmailsGoToSpamGuide() {
  return (
    <div>
      <SEO
        title="Why Are My Emails Going to Spam?"
        description="Learn why your business emails end up in spam folders and how to fix it with email authentication. Understand email spoofing and how SPF, DKIM, and DMARC protect your deliverability."
        canonical="/guides/why-emails-go-to-spam"
        ogType="article"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Help Center
          </Link>
          <h1 className="mt-8 text-4xl font-bold tracking-tight sm:text-5xl">
            Why Are My Emails Going to Spam?
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
            The #1 reason business emails end up in spam folders — and how to fix it.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">

          {/* Section 1 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              The Problem: Email Providers Don't Trust You (Yet)
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                When you send an email from your business domain (like sales@yourcompany.com),
                the receiving email provider (Gmail, Outlook, Yahoo, etc.) has to decide:
                <strong className="text-foreground"> Is this email legitimate, or is it spam?</strong>
              </p>
              <p>
                Here's the thing — anyone can claim to send email from your domain. Scammers do it all
                the time. It's called <strong className="text-foreground">email spoofing</strong>, and it's shockingly easy to do.
              </p>
              <p>
                So email providers developed a system to verify that emails actually come from
                authorized senders. If you haven't set up this verification, your emails look
                suspicious — just like the fake ones.
              </p>
            </div>

            <div className="mt-8 rounded-2xl border-l-4 border-amber-500 bg-amber-50 p-6 dark:bg-amber-950/30">
              <p className="text-lg font-medium text-amber-800 dark:text-amber-200">
                Without email authentication, Gmail and other providers can't tell the difference
                between your real emails and fake emails from scammers pretending to be you.
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 2 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              The Solution: Email Authentication
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Email authentication is a set of DNS records that prove you're authorized to send
                email from your domain. Think of it like a digital ID card for your emails.
              </p>
              <p>
                There are three parts that work together:
              </p>
            </div>

            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              <div className="rounded-2xl border bg-card p-6 text-center">
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">SPF</div>
                <div className="mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Sender Policy Framework
                </div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  Lists the servers allowed to send email for your domain. Like a guest list for
                  email servers.
                </p>
              </div>
              <div className="rounded-2xl border bg-card p-6 text-center">
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">DKIM</div>
                <div className="mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  DomainKeys Identified Mail
                </div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  Adds a digital signature to your emails that proves they haven't been tampered with.
                </p>
              </div>
              <div className="rounded-2xl border bg-card p-6 text-center">
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">DMARC</div>
                <div className="mt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Domain-based Authentication
                </div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                  Tells providers what to do with emails that fail checks, and sends you reports.
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 3 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Why This Matters for Your Business
            </h2>
            <div className="mt-8 space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Your emails reach customers</h3>
                  <p className="mt-1 text-muted-foreground">
                    Properly authenticated emails are far less likely to be marked as spam.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Protects your reputation</h3>
                  <p className="mt-1 text-muted-foreground">
                    DMARC prevents scammers from sending fake emails that look like they're from you.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Required by major providers</h3>
                  <p className="mt-1 text-muted-foreground">
                    Google and Yahoo now require email authentication for bulk senders. Even small businesses benefit.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 4 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Check Your Current Status
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Not sure if you have email authentication set up? Use our free domain checker to
              find out in seconds — no account required.
            </p>

            <div className="mt-10 flex justify-center">
              <Link
                to="/tools"
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-105"
              >
                Check Your Domain Free
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 5 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Next Steps
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              If your domain check shows missing or misconfigured records, don't worry — we'll
              help you fix it. GetSent provides:
            </p>
            <ul className="mt-6 space-y-3 text-lg text-muted-foreground">
              <li className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Step-by-step setup guides for your specific DNS provider</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Automatic DMARC report monitoring so you can see who's sending email as you</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="h-6 w-6 flex-shrink-0 text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Alerts when something goes wrong</span>
              </li>
            </ul>
          </section>

          {/* CTA Box */}
          <section className="rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 p-8 dark:from-indigo-950/50 dark:to-purple-950/50 sm:p-10">
            <h3 className="text-2xl font-bold">Ready to fix your email deliverability?</h3>
            <p className="mt-4 text-lg text-muted-foreground">
              Create a free GetSent account and we'll walk you through setting up email
              authentication for your domain.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/register"
                className="inline-flex rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white hover:bg-indigo-500 transition-colors"
              >
                Get Started Free
              </Link>
              <Link
                to="/resources"
                className="inline-flex rounded-lg border border-border bg-background px-6 py-3 text-base font-semibold hover:bg-muted transition-colors"
              >
                Explore More Guides
              </Link>
            </div>
          </section>

        </div>
      </article>
    </div>
  );
}
